import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Urls } from 'js/src/client';
import { AuthQueryParams } from 'js/src/components/auth/auth-consts';
import {
    GOOGLE_ONE_TAP,
    getLastLoginMethod,
    setLastLoginMethod,
} from 'js/src/components/auth/common/lastLoginMethod';
import { useAuthReasonToast } from 'js/src/components/auth/common/useAuthReasonToast';
import { PasswordWithForgotLink } from 'js/src/components/auth/common/Password';
import SocialButton, { SOCIAL_BUTTON_PROVIDERS } from 'js/src/components/auth/common/SocialButton';
import { useFormFeedback } from 'js/src/components/auth/common/useFormFeedback';
import { useForceUpdate } from 'js/src/components/common/hooks/useForceUpdate';
import { Toast } from 'js/src/components/common/Toast';
import Button, {
    BUTTON_COLORS,
    BUTTON_TYPE,
    BUTTON_VARIANTS,
} from 'js/src/components/common/ui/Button';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import TextInput, {
    AUTOCOMPLETE_VALUES,
    TEXT_INPUT_VARIANTS,
    type TypeInputChangeEvent,
} from 'js/src/components/common/ui/TextInput';
import { loginSignupOperation } from 'js/src/data/account';
import { buildSocialAuthUrl, startSocialAuth } from 'js/src/data/account/utils';
import { isEmailValid } from 'js/src/libs/custom-utils';
import Navigation from 'js/src/libs/navigation';
import { getQueryParam } from 'js/src/libs/querystring';
import QueryStringManipulator from 'js/src/libs/querystring-manipulator';

type Props = {
    currentPath: string;
    facebookAuthUrl: string;
    googleAuthUrl: string;
    forgotPasswordUrl: string;
};

const LoginForm = React.memo(function LoginForm({
    currentPath,
    facebookAuthUrl,
    googleAuthUrl,
    forgotPasswordUrl,
}: Props) {
    const [emailVal, setInitialEmail] = useState('');
    const [passwordVal, setPasswordVal] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [showAllMethods, setShowAllMethods] = useState(false);
    const [formFeedback, setFormFeedback] = useFormFeedback({
        email: '',
        password: '',
        global: '',
    });
    const { forceUpdate } = useForceUpdate();
    const lastLoginMethod = useMemo(() => getLastLoginMethod(), []);

    useEffect(() => {
        if (formFeedback.global !== '') {
            setShowEmailForm(true);
        }
    }, [formFeedback]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (getQueryParam(AuthQueryParams.EXISTING_ACCOUNT) === '1') {
                Toast.info(
                    'Looks like you already have a Lumen5 account. Please log in instead.',
                    undefined,
                    undefined,
                    8000,
                );
                const newUrl = QueryStringManipulator.removeQueryStringParameter(
                    window.location.href,
                    AuthQueryParams.EXISTING_ACCOUNT,
                );
                Navigation.replaceState(newUrl);
                forceUpdate();
            }
            if (getQueryParam(AuthQueryParams.MUST_SIGNUP) === '1') {
                Toast.info(
                    'This account does not exist. Please sign up first.',
                    undefined,
                    undefined,
                    8000,
                );
                const newUrl = QueryStringManipulator.removeQueryStringParameter(
                    window.location.href,
                    AuthQueryParams.MUST_SIGNUP,
                );
                Navigation.replaceState(newUrl);
                forceUpdate();
            }
            if (getQueryParam(AuthQueryParams.DIFFERENT_METHOD) === '1') {
                Toast.info(
                    'Your account is registered with a different method (facebook, google, email/password). Please log in with the correct method.',
                    undefined,
                    undefined,
                    8000,
                );
                const newUrl = QueryStringManipulator.removeQueryStringParameter(
                    window.location.href,
                    AuthQueryParams.DIFFERENT_METHOD,
                );
                Navigation.replaceState(newUrl);
                forceUpdate();
            }
        }, 1000);
        return () => clearTimeout(timeoutId);
    }, [forceUpdate]);

    useAuthReasonToast();

    const handleShowEmailForm = useCallback(() => {
        setShowEmailForm(true);
    }, []);

    const handleShowAllMethods = useCallback(() => {
        setShowAllMethods(true);
    }, []);

    const emailOnChangeHandler = useCallback(
        (e: TypeInputChangeEvent) => setInitialEmail(e.target.value),
        [],
    );
    const passwordOnChangeHandler = useCallback(
        (e: TypeInputChangeEvent) => setPasswordVal(e.target.value),
        [],
    );

    const facebookOnclick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setLastLoginMethod(SOCIAL_BUTTON_PROVIDERS.FACEBOOK);
            startSocialAuth(buildSocialAuthUrl(facebookAuthUrl, currentPath));
        },
        [facebookAuthUrl, currentPath],
    );

    const googleOnclick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setLastLoginMethod(SOCIAL_BUTTON_PROVIDERS.GOOGLE);
            startSocialAuth(buildSocialAuthUrl(googleAuthUrl, currentPath));
        },
        [googleAuthUrl, currentPath],
    );

    const ssoOnclick = useCallback(() => {
        setLastLoginMethod(SOCIAL_BUTTON_PROVIDERS.SSO);
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setFormFeedback({ type: 'CLEAR_ERRORS' });

        const lowerCaseEmail = emailVal.trim().toLowerCase();

        if (!isEmailValid(lowerCaseEmail)) {
            setFormFeedback({
                type: 'SET_ERROR',
                payload: {
                    email: 'Please provide an email.',
                },
            });

            return false;
        }

        if (passwordVal === '') {
            setFormFeedback({
                type: 'SET_ERROR',
                payload: {
                    password: 'Please provide a valid password.',
                },
            });

            return false;
        }

        setIsLoggingIn(true);
        setLastLoginMethod('email');
        loginSignupOperation({
            email: lowerCaseEmail,
            password: passwordVal,
            is_signup: '0',
            invite_uuid: getQueryParam(AuthQueryParams.INVITE_UUID),
        })
            .done((response: { next?: string; invited?: string; mfa_url?: string }) => {
                // redirect via redirect callback url
                const { next = '', invited = false, mfa_url = '' } = response;
                if (mfa_url) {
                    window.location.href = mfa_url;
                } else {
                    window.location.href =
                        invited === '1'
                            ? QueryStringManipulator.updateQueryStringParameter(
                                  next,
                                  'invited',
                                  invited,
                              )
                            : next;
                }
            })
            .fail((xhr) => {
                if (xhr.getResponseHeader('cf-mitigated') === 'challenge') {
                    // Cloudflare's Web Application Firewall is challenging that this
                    // is a valid person (and not a bot). We need to reload the page
                    // to show the user the challenge page. If they pass that challenge,
                    // the next time they submit the form, it should go through.
                    window.location.reload();
                    return;
                }
                const { responseJSON = {} } = xhr;
                setFormFeedback({
                    type: 'SET_ERROR',
                    // backend messages indicate which field has errors
                    // ie. responseJSON = { 'password': 'errorMessage' }
                    payload: responseJSON,
                });

                setIsLoggingIn(false);
            });
    };

    const queryString = window.location.search || '';

    const renderLastUsedButton = () => {
        switch (lastLoginMethod) {
            case SOCIAL_BUTTON_PROVIDERS.GOOGLE:
            case GOOGLE_ONE_TAP:
                return (
                    <SocialButton
                        provider={SOCIAL_BUTTON_PROVIDERS.GOOGLE}
                        onClick={googleOnclick}
                        text="Log in with Google"
                        data-testid="google-login"
                    />
                );
            case SOCIAL_BUTTON_PROVIDERS.FACEBOOK:
                return (
                    <SocialButton
                        provider={SOCIAL_BUTTON_PROVIDERS.FACEBOOK}
                        onClick={facebookOnclick}
                        text="Log in with Facebook"
                        data-testid="facebook-login"
                    />
                );
            case SOCIAL_BUTTON_PROVIDERS.SSO:
                return (
                    <SocialButton
                        provider={SOCIAL_BUTTON_PROVIDERS.SSO}
                        href={Urls.SSO_VERIFICATION + queryString}
                        onClick={ssoOnclick}
                        text="Single sign-on (SSO)"
                        data-testid="sso-login"
                    />
                );
            default:
                return null;
        }
    };

    const renderEmailForm = () => (
        <form
            id="login-form"
            method="post"
            className="form-sidebar"
            data-testid="login-form"
            onSubmit={handleSubmit}
        >
            {formFeedback.global !== '' && (
                <Callout
                    msgType={CALLOUT_TYPES.ERROR}
                    text={<span dangerouslySetInnerHTML={{ __html: formFeedback.global }} />}
                />
            )}

            <div className="mb-l5-16">
                <TextInput
                    text={emailVal}
                    labelText="Work email"
                    variant={TEXT_INPUT_VARIANTS.STANDARD}
                    autoFocus={true}
                    onChange={emailOnChangeHandler}
                    formFeedbackMessage={formFeedback.email}
                    data-testid="email"
                    autocomplete={AUTOCOMPLETE_VALUES.EMAIL}
                    ignorePasswordManagers={false}
                />
            </div>

            <div className="password-container mb-l5-24">
                <PasswordWithForgotLink
                    passwordVal={passwordVal}
                    onChange={passwordOnChangeHandler}
                    forgotPasswordUrl={forgotPasswordUrl}
                    formFeedbackMessage={formFeedback.password}
                />
            </div>

            <div className="mb-l5-16">
                <Button
                    className="d-block w-100"
                    type={BUTTON_TYPE.SUBMIT}
                    text={isLoggingIn ? 'Logging in...' : 'Log in'}
                    data-testid="submit-button"
                    disabled={isLoggingIn}
                />
            </div>
        </form>
    );

    const renderAllMethods = () => (
        <>
            <div className="mb-l5-16">
                <SocialButton
                    provider={SOCIAL_BUTTON_PROVIDERS.GOOGLE}
                    onClick={googleOnclick}
                    text="Log in with Google"
                    data-testid="google-login"
                />
            </div>
            <div className="mb-l5-16">
                <SocialButton
                    provider={SOCIAL_BUTTON_PROVIDERS.FACEBOOK}
                    onClick={facebookOnclick}
                    text="Log in with Facebook"
                    data-testid="facebook-login"
                />
            </div>
            <div className="mb-l5-16">
                <SocialButton
                    provider={SOCIAL_BUTTON_PROVIDERS.SSO}
                    href={Urls.SSO_VERIFICATION + queryString}
                    onClick={ssoOnclick}
                    text="Single sign-on (SSO)"
                    data-testid="sso-login"
                />
            </div>
            <div className="divider text-muted my-l5-32">
                <hr />
                <span>or</span>
                <hr />
            </div>
            {!showEmailForm ? (
                <div>
                    <div className="mb-l5-24">
                        <Button
                            variant={BUTTON_VARIANTS.OUTLINE}
                            className="d-block w-100"
                            onClick={handleShowEmailForm}
                            text="Log in with email"
                            data-testid="email-login-btn"
                        />
                    </div>
                </div>
            ) : (
                renderEmailForm()
            )}
        </>
    );

    const showLastUsedShortcut = lastLoginMethod && !showAllMethods && !showEmailForm;

    return (
        <div>
            {showLastUsedShortcut ? (
                <>
                    {lastLoginMethod === 'email' ? (
                        renderEmailForm()
                    ) : (
                        <div className="mb-l5-16">{renderLastUsedButton()}</div>
                    )}
                    <div className="text-center mt-l5-24">
                        <Button
                            variant={BUTTON_VARIANTS.LINK}
                            color={BUTTON_COLORS.TERTIARY}
                            onClick={handleShowAllMethods}
                            text="Log in a different way"
                            data-testid="show-all-methods"
                        />
                    </div>
                </>
            ) : (
                renderAllMethods()
            )}
            <div className="text-center mt-l5-32">
                <span>Need a Lumen5 account? </span>
                <a
                    href={Urls.SIGNUP + queryString}
                    className="fw-semibold text-decoration-none"
                    data-testid="signup-link"
                >
                    Sign up
                </a>
            </div>
        </div>
    );
});

export default LoginForm;
